// 3rd party libs
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Components
import { Checkbox, InputLabel } from 'components';
import {
  components,
  DropdownIndicatorProps,
  GroupBase,
  MultiValueProps,
  OptionProps,
  StylesConfig
} from 'react-select';
import { themeColors } from 'themes';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import ChevronUp from 'icons/indexed-icons/ChevronUp';
import ChevronDown from 'icons/indexed-icons/ChevronDown';
import { InputVariant } from 'types';

interface CustomAsyncSelectProps
  extends AsyncProps<unknown, boolean, GroupBase<unknown>>,
    InputVariant {
  id?: string;
  mandatory?: boolean;
  label?: string;
  isCheckboxInput?: boolean;
  isDisabled?: boolean;
}

const StyledAsyncSelect = styled(AsyncSelect)`
  font-family: ${(props) => props.theme.typography.family || 'sans-serif'};
  font-size: ${(props) => props.theme.typography.components.input.size || '0.875rem'};
  line-height: ${(props) => props.theme.typography.components.input.lineHeight || '1.125rem'};
  > div:nth-of-type(1) {
    border: 1.5px solid ${themeColors.tableborder};
    border-radius: 6px;
    &:hover {
      border-color: ${themeColors.primaryBlue5};
    }
    &:focus-within {
      outline: 0;
      border-color: ${themeColors.tableborder};
      box-shadow: none;
    }
  }
  .multi-value-group {
    &.max-width-2 {
      max-width: 33%;
    }
    &.max-width-1 {
      max-width: 50%;
    }
    &.max-width-3 {
      max-width: 25%;
    }
  }
  div {
    color: ${themeColors.lightGrey8};
    font-size: 14px;
  }

  .custom-option {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    height: 100%;
    label {
      width: 100%;
      cursor: pointer;
    }
    p {
      margin: 0;
    }
    &.selected {
      background-color: ${themeColors.primaryBlue5};
      color: ${themeColors.white};
    }
  }

  .custom-checkbox-option {
    color: ${themeColors.textLightBlack};
    &:hover {
      background-color: ${themeColors.lightGrey10};
    }
    &.selected {
      background-color: ${themeColors.white};
      color: ${themeColors.textLightBlack};
    }
  }
`;

const SelectContainer = styled.div`
  width: 100%;
`;

const LabelContainer = styled.div`
  sup {
    vertical-align: baseline;
  }
`;

const CustomDropdownIndicator = styled.div`
  padding-right: 10px;
  svg {
    width: 11px;
  }
  &:hover {
    cursor: pointer;
  }
`;

const MultiValueGroup = styled.div`
  border: 1px solid ${themeColors.primaryBlue5};
  border-radius: 2rem;
  > div {
    background-color: ${themeColors.white};
    border-radius: 2rem;
  }
  div[role='button']:hover {
    background-color: transparent;
    color: ${themeColors.primaryBlue5};
    cursor: pointer;
  }
  .custom-selected-count {
    padding: 5px 8px;
  }
`;

const GroupHeading = styled.div`
  font-weight: 600;
  color: ${themeColors.black} !important;
  text-transform: capitalize;
  margin-left: -2px;
`;

export const IndicatorSeparator = () => {
  return <span></span>;
};

export const DropdownIndicator = (props: DropdownIndicatorProps) => {
  return (
    <CustomDropdownIndicator>
      {props.selectProps.menuIsOpen ? (
        <ChevronUp />
      ) : (
        <ChevronDown color={themeColors.lightGrey8} />
      )}
    </CustomDropdownIndicator>
  );
};

const formatGroupLabel = (data: GroupBase<unknown>) => <GroupHeading>{data.label}</GroupHeading>;

const customStyles: StylesConfig = {
  menu: (stylesProvided) => ({
    ...stylesProvided,
    overflow: 'auto',
    marginTop: 1,
    boxShadow: '0 14px 24px rgba(0, 0, 0, 0.25)',
    borderRadius: '10px'
  }),
  menuList: (stylesProvided) => ({
    ...stylesProvided,
    paddingTop: 0,
    paddingBottom: 0,
    overflow: 'auto'
  }),
  valueContainer: (stylesProvided) => ({
    ...stylesProvided,
    gap: '4px'
  }),
  control: (stylesProvided) => ({
    ...stylesProvided,
    height: '40px'
  }),
  loadingIndicator: (stylesProvided) => ({
    ...stylesProvided,
    fontSize: '6px !important'
  })
};

const CustomAsyncSelect = ({
  id,
  mandatory,
  label,
  isMulti,
  isCheckboxInput,
  isDisabled,
  ...restProps
}: CustomAsyncSelectProps): ReactElement => {
  const selectRef = useRef<HTMLElement>(null);
  const [selectWidth, setSelectWidth] = useState(0);

  useEffect(() => {
    if (selectRef.current) {
      setSelectWidth(selectRef.current.offsetWidth);
    }
  }, [selectRef]);

  const MultiValue = (props: MultiValueProps) => {
    const { index, getValue } = props;
    const CUSTOM_LIMIT = selectWidth > 475 ? 2 : 1;
    const hiddenLength = getValue().length - CUSTOM_LIMIT;
    return index < CUSTOM_LIMIT ? (
      <MultiValueGroup className={`multi-value-group max-width-${CUSTOM_LIMIT}`}>
        <components.MultiValue {...props} />
      </MultiValueGroup>
    ) : index === CUSTOM_LIMIT ? (
      <MultiValueGroup className="multi-value-group">
        <div className="custom-selected-count">{`+${hiddenLength}`}</div>
      </MultiValueGroup>
    ) : null;
  };

  const Option = (props: OptionProps) => {
    return (
      <div>
        <components.Option
          {...props}
          className={`custom-option ${isCheckboxInput && 'custom-checkbox-option'} ${
            props.isSelected && 'selected'
          }`}
        >
          {isCheckboxInput && (
            <Checkbox height={16} width={16} checked={props.isSelected} onChange={() => null} />
          )}
          <label>{props.label}</label>
        </components.Option>
      </div>
    );
  };

  return (
    <>
      {label && (
        <LabelContainer>
          <InputLabel id={id} mandatory={mandatory}>
            {label}
          </InputLabel>
        </LabelContainer>
      )}
      <SelectContainer ref={selectRef}>
        <StyledAsyncSelect
          className="custom-select"
          isMulti={isMulti}
          isSearchable
          hideSelectedOptions={false}
          isClearable={false}
          styles={customStyles}
          components={{
            IndicatorSeparator,
            DropdownIndicator,
            Option,
            MultiValue
          }}
          closeMenuOnSelect={!isMulti}
          isDisabled={isDisabled}
          formatGroupLabel={formatGroupLabel}
          {...restProps}
        />
      </SelectContainer>
    </>
  );
};

export default CustomAsyncSelect;
